<!-- src/components/course/FAQForm.vue -->
<template>
	<div class="faq-form" @keydown.esc="handleEscKey">
		<div class="faq-form__header">
			<div class="faq-form__header-content">
				<div class="faq-form__icon">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
						<path
							d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
							stroke="currentColor"
							stroke-width="2"
						/>
						<path
							d="M9 9C9 7.34315 10.3431 6 12 6C13.6569 6 15 7.34315 15 9C15 10.3062 14.1652 11.4175 13 11.8293V13"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
						/>
						<circle cx="12" cy="17" r="1" fill="currentColor" />
					</svg>
				</div>
				<h3 class="faq-form__title">
					{{ isEdit ? 'Редактирование вопроса' : 'Новый вопрос' }}
				</h3>
			</div>
			<button
				class="faq-form__close-btn"
				type="button"
				@click="handleCancel"
				title="Закрыть (Esc)"
				:disabled="saving"
			>
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
					<path
						d="M18 6L6 18M6 6L18 18"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
					/>
				</svg>
			</button>
		</div>

		<form class="faq-form__form" @submit.prevent="handleSubmit">
			<!-- Вопрос -->
			<div class="faq-form__field" :class="{ 'faq-form__field--error': errors.question }">
				<label class="faq-form__label" for="faq-question">
					Вопрос <span class="faq-form__required">*</span>
				</label>
				<div class="faq-form__input-wrapper">
					<input
						ref="questionInput"
						id="faq-question"
						v-model.trim="formData.question"
						class="faq-form__input"
						type="text"
						placeholder="Например: Как получить сертификат?"
						maxlength="500"
						:disabled="saving"
						@input="clearError('question')"
						@blur="validateField('question')"
					/>
					<span class="faq-form__char-count" :class="{ 'faq-form__char-count--warning': formData.question.length > 450 }">
						{{ formData.question.length }}/500
					</span>
				</div>
				<transition name="fade">
					<span v-if="errors.question" class="faq-form__error">
						<svg class="faq-form__error-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
							<path
								d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
								stroke="currentColor"
								stroke-width="2"
								stroke-linejoin="round"
							/>
							<path d="M12 9v4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
							<path d="M12 17h.01" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
						</svg>
						{{ errors.question }}
					</span>
				</transition>
			</div>

			<!-- Ответ -->
			<div class="faq-form__field" :class="{ 'faq-form__field--error': errors.answer }">
				<label class="faq-form__label" for="faq-answer">
					Ответ <span class="faq-form__required">*</span>
				</label>
				<div class="faq-form__textarea-wrapper">
					<textarea
						id="faq-answer"
						v-model.trim="formData.answer"
						class="faq-form__textarea"
						placeholder="Подробно опишите ответ на вопрос..."
						rows="5"
						maxlength="5000"
						:disabled="saving"
						@input="clearError('answer'); autoResize($event)"
						@blur="validateField('answer')"
					></textarea>
					<span class="faq-form__char-count" :class="{ 'faq-form__char-count--warning': formData.answer.length > 4500 }">
						{{ formData.answer.length }}/5000
					</span>
				</div>
				<transition name="fade">
					<span v-if="errors.answer" class="faq-form__error">
						<svg class="faq-form__error-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
							<path
								d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
								stroke="currentColor"
								stroke-width="2"
								stroke-linejoin="round"
							/>
							<path d="M12 9v4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
							<path d="M12 17h.01" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
						</svg>
						{{ errors.answer }}
					</span>
				</transition>
			</div>

			<!-- Действия -->
			<div class="faq-form__actions">
				<button
					class="faq-form__btn faq-form__btn--cancel"
					type="button"
					@click="handleCancel"
					:disabled="saving"
				>
					Отменить
				</button>
				<button
					class="faq-form__btn faq-form__btn--submit"
					type="submit"
					:disabled="saving || !isFormValid"
				>
					<span v-if="saving" class="faq-form__spinner"></span>
					<span>{{ submitButtonText }}</span>
				</button>
			</div>
		</form>
	</div>
</template>

<script>
export default {
	name: 'FAQForm',
	props: {
		faq: {
			type: Object,
			default: null,
		},
		saving: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['submit', 'cancel'],
	data() {
		return {
			formData: {
				question: '',
				answer: '',
				is_published: true,
			},
			errors: {
				question: '',
				answer: '',
			},
			isDirty: false,
		}
	},
	computed: {
		isEdit() {
			return !!this.faq?.id
		},
		isFormValid() {
			return (
				this.formData.question.trim().length >= 5 &&
				this.formData.answer.trim().length >= 10 &&
				!this.errors.question &&
				!this.errors.answer
			)
		},
		submitButtonText() {
			if (this.saving) return 'Сохранение...'
			return this.isEdit ? 'Сохранить изменения' : 'Создать вопрос'
		},
	},
	watch: {
		formData: {
			handler() {
				this.isDirty = true
			},
			deep: true,
		},
	},
	mounted() {
		this.initForm()
		this.$nextTick(() => {
			this.$refs.questionInput?.focus()
		})
	},
	methods: {
		initForm() {
			if (this.faq) {
				this.formData = {
					question: this.faq.question || '',
					answer: this.faq.answer || '',
					is_published: this.faq.is_published ?? false,
				}
			}
			this.isDirty = false
		},

		validateField(field) {
			const value = this.formData[field]?.trim() || ''

			if (field === 'question') {
				if (!value) {
					this.errors.question = 'Введите вопрос'
				} else if (value.length < 5) {
					this.errors.question = 'Минимум 5 символов'
				} else if (value.length > 500) {
					this.errors.question = 'Максимум 500 символов'
				} else {
					this.errors.question = ''
				}
			}

			if (field === 'answer') {
				if (!value) {
					this.errors.answer = 'Введите ответ'
				} else if (value.length < 10) {
					this.errors.answer = 'Минимум 10 символов'
				} else if (value.length > 5000) {
					this.errors.answer = 'Максимум 5000 символов'
				} else {
					this.errors.answer = ''
				}
			}

			return !this.errors[field]
		},

		validateForm() {
			const questionValid = this.validateField('question')
			const answerValid = this.validateField('answer')
			return questionValid && answerValid
		},

		clearError(field) {
			this.errors[field] = ''
		},

		autoResize(event) {
			const textarea = event.target
			textarea.style.height = 'auto'
			textarea.style.height = Math.min(textarea.scrollHeight, 300) + 'px'
		},

		handleSubmit() {
			if (this.saving) return
			if (!this.validateForm()) return

			this.$emit('submit', {
				question: this.formData.question.trim(),
				answer: this.formData.answer.trim(),
				is_published: this.formData.is_published,
			})
		},

		handleCancel() {
			if (this.isDirty && this.formData.question.trim()) {
				if (!confirm('Отменить редактирование? Несохранённые данные будут потеряны.')) {
					return
				}
			}
			this.$emit('cancel')
		},

		handleEscKey(event) {
			if (event.key === 'Escape' && !this.saving) {
				this.handleCancel()
			}
		},
	},
}
</script>

<style scoped>
.faq-form {
	background: #ffffff;
	border-radius: 16px;
	padding: 28px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
	max-width: 560px;
	width: 100%;
	max-height: none;
	overflow: visible;
}

.faq-form__header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 24px;
	padding-bottom: 20px;
	border-bottom: 1px solid #eef0f2;
}

.faq-form__header-content {
	display: flex;
	align-items: center;
	gap: 12px;
}

.faq-form__icon {
	width: 44px;
	height: 44px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: linear-gradient(135deg, #0095ec 0%, #0077be 100%);
	border-radius: 12px;
	color: #ffffff;
}

.faq-form__title {
	color: #1a1d21;
	font-family: 'Poppins', sans-serif;
	font-size: 20px;
	font-weight: 600;
	margin: 0;
	line-height: 1.3;
}

.faq-form__close-btn {
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #f5f7f9;
	border: none;
	border-radius: 10px;
	color: #6b7280;
	cursor: pointer;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.faq-form__close-btn:hover:not(:disabled) {
	background: #fee2e2;
	color: #dc2626;
}

.faq-form__close-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.faq-form__form {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.faq-form__field {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.faq-form__field--error .faq-form__input,
.faq-form__field--error .faq-form__textarea {
	border-color: #dc2626;
	background: #fef2f2;
}

.faq-form__label {
	color: #374151;
	font-family: 'Poppins', sans-serif;
	font-size: 14px;
	font-weight: 500;
	display: flex;
	align-items: center;
	gap: 4px;
}

.faq-form__required {
	color: #dc2626;
	font-weight: 600;
}

.faq-form__input-wrapper,
.faq-form__textarea-wrapper {
	position: relative;
}

.faq-form__input,
.faq-form__textarea {
	width: 100%;
	padding: 14px 16px;
	padding-right: 70px;
	border: 2px solid #e5e7eb;
	border-radius: 10px;
	font-family: 'Poppins', sans-serif;
	font-size: 14px;
	color: #1a1d21;
	background: #ffffff;
	transition: all 0.2s ease;
	box-sizing: border-box;
}

.faq-form__input::placeholder,
.faq-form__textarea::placeholder {
	color: #9ca3af;
}

.faq-form__input:focus,
.faq-form__textarea:focus {
	outline: none;
	border-color: #0095ec;
	box-shadow: 0 0 0 4px rgba(0, 149, 236, 0.1);
}

.faq-form__input:disabled,
.faq-form__textarea:disabled {
	background: #f9fafb;
	cursor: not-allowed;
}

.faq-form__textarea {
	resize: none;
	min-height: 120px;
	line-height: 1.6;
}

.faq-form__char-count {
	position: absolute;
	right: 12px;
	bottom: 12px;
	font-size: 11px;
	color: #9ca3af;
	font-family: 'Poppins', sans-serif;
	pointer-events: none;
}

.faq-form__char-count--warning {
	color: #f59e0b;
	font-weight: 500;
}

.faq-form__error {
	color: #dc2626;
	font-size: 12px;
	font-family: 'Poppins', sans-serif;
	display: flex;
	align-items: center;
	gap: 4px;
}


.faq-form__error-icon {
	flex-shrink: 0;
}

/* Actions */
.faq-form__actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 8px;
	padding-top: 20px;
	border-top: 1px solid #eef0f2;
}

.faq-form__btn {
	padding: 12px 24px;
	border: none;
	border-radius: 10px;
	font-family: 'Poppins', sans-serif;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	min-width: 140px;
}

.faq-form__btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
	transform: none !important;
}

.faq-form__btn--cancel {
	background: #f3f4f6;
	color: #4b5563;
}

.faq-form__btn--cancel:hover:not(:disabled) {
	background: #e5e7eb;
}

.faq-form__btn--submit {
	background: linear-gradient(135deg, #0095ec 0%, #0077be 100%);
	color: #ffffff;
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.25);
}

.faq-form__btn--submit:hover:not(:disabled) {
	transform: translateY(-2px);
	box-shadow: 0 6px 20px rgba(0, 149, 236, 0.35);
}

.faq-form__btn--submit:active:not(:disabled) {
	transform: translateY(0);
}

/* Spinner */
.faq-form__spinner {
	width: 16px;
	height: 16px;
	border: 2px solid rgba(255, 255, 255, 0.3);
	border-top-color: #ffffff;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}

/* Responsive */
@media (min-width: 769px) and (max-width: 1366px) {
	.faq-form {
		max-width: 720px;
		padding: 32px;
	}
}

@media (min-width: 1367px) and (max-width: 1600px) {
	.faq-form {
		max-width: 780px;
		padding: 34px;
	}
}

@media (min-width: 1601px) and (max-width: 1800px) {
	.faq-form {
		max-width: 860px;
		padding: 36px;
	}
}

@media (min-width: 1801px) {
	.faq-form {
		max-width: 900px;
		padding: 36px;
	}
}

@media (max-width: 768px) {
	.faq-form {
		max-height: 90vh;
		overflow-y: auto;
	}
}

@media (max-width: 480px) {
	.faq-form {
		padding: 20px;
		border-radius: 12px;
	}

	.faq-form__header-content {
		gap: 10px;
	}

	.faq-form__icon {
		width: 38px;
		height: 38px;
	}

	.faq-form__title {
		font-size: 18px;
	}

	.faq-form__actions {
		flex-direction: column-reverse;
	}

	.faq-form__btn {
		width: 100%;
	}
}
</style>
